import { useState } from "react";
import { Copy, Loader2, MailQuestion, Send } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Chip } from "@/components/common/Chrome";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { api, apiError } from "@/lib/api";

const days = (stamp) => {
  if (!stamp) return 0;
  return Math.floor((Date.now() - new Date(stamp).getTime()) / 86400000);
};

const asked = (n) => (n === 0 ? "asked today" : n === 1 ? "asked yesterday" : `asked ${n} days ago`);

const DONOR_FIXABLE = ["title", "first_name", "last_name", "address_line", "postcode", "tax_id"];

/** Sends the donor a single-use link to fill in their own missing claim detail. */
export const AskDonorButton = ({ row, onDone }) => {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const ask = row.ask;
  const askable = (row.missing || []).some((m) => DONOR_FIXABLE.includes(m));

  const send = async () => {
    setBusy(true);
    try {
      const res = await api.post("/claims/ask-donor", { donation_id: row.donation_id });
      setResult(res.data);
      toast.success(res.data.email?.sent
        ? `Emailed ${res.data.request_ref} to the donor`
        : "Link ready — email is not switched on yet, so copy it and send it yourself");
      await onDone();
    } catch (e) { toast.error(apiError(e)); }
    setBusy(false);
  };

  return (
    <div className="flex items-center justify-end gap-1.5">
      {ask && ask.status === "completed" && (
        <Chip className="border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300">
          donor replied
        </Chip>
      )}
      {ask && ask.status === "sent" && (
        <Chip data-testid={`claim-ask-state-${row.reference}`}
              className={ask.expired
                ? "border-red-500/30 bg-red-500/10 text-red-700 dark:text-red-300"
                : "border-border bg-muted text-muted-foreground"}>
          {ask.expired ? "link expired" : asked(days(ask.asked_at))}
          {ask.ask_count > 1 ? ` · ${ask.ask_count}×` : ""}
        </Chip>
      )}
      {ask?.link && (
        <Button size="sm" variant="ghost" data-testid={`claim-ask-copy-${row.reference}`}
                title="Copy the donor's link"
                onClick={() => {
                  navigator.clipboard.writeText(ask.link);
                  toast.success("Link copied — send it by WhatsApp or email");
                }}
                className="h-7 rounded-full px-2 text-[10px] text-muted-foreground">
          <Copy className="h-3 w-3" />
        </Button>
      )}
      <Button size="sm" variant="outline" onClick={send} disabled={busy || !askable}
              data-testid={`claim-ask-${row.reference}`}
              title={askable ? "" : "Nothing here the donor can supply"}
              className="h-7 rounded-full border-border px-2.5 text-[10px]">
        {busy ? <Loader2 className="mr-1 h-3 w-3 animate-spin" />
          : <MailQuestion className="mr-1 h-3 w-3" />}
        {ask ? "Ask again" : "Ask the donor"}
      </Button>

      <Dialog open={!!result} onOpenChange={(o) => !o && setResult(null)}>
        <DialogContent className="max-w-md" data-testid="claim-ask-result">
          <DialogHeader>
            <DialogTitle>Send this to {row.donor_name}</DialogTitle>
          </DialogHeader>
          <p className="text-[12px] text-muted-foreground">
            {result?.email?.sent
              ? `Emailed to ${result?.email && row.donor_name}. `
              : "Email delivery is not switched on yet, so nothing has left the building. "}
            The link works until {result?.expires_on} and stops working once they use it.
          </p>
          <div className="flex items-center gap-2 rounded-xl border border-border bg-muted/30 p-2.5">
            <code className="flex-1 truncate text-[10.5px] text-foreground">{result?.link}</code>
            <Button size="sm" variant="ghost" data-testid="claim-ask-result-copy"
                    onClick={() => {
                      navigator.clipboard.writeText(result.link);
                      toast.success("Link copied");
                    }}
                    className="h-7 rounded-full px-2 text-[10px]">
              <Copy className="h-3 w-3" />
            </Button>
          </div>
          <div className="flex flex-wrap gap-2">
            {result?.whatsapp_share_url && (
              <Button asChild size="sm" variant="outline"
                      className="rounded-full border-border text-[11px]">
                <a href={result.whatsapp_share_url} target="_blank" rel="noreferrer"
                   data-testid="claim-ask-whatsapp">
                  <Send className="mr-1.5 h-3 w-3" /> Send on WhatsApp
                </a>
              </Button>
            )}
            {result?.mailto_url && (
              <Button asChild size="sm" variant="outline"
                      className="rounded-full border-border text-[11px]">
                <a href={result.mailto_url} data-testid="claim-ask-mailto">
                  <Send className="mr-1.5 h-3 w-3" /> Draft an email
                </a>
              </Button>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
